/* eslint-disable @typescript-eslint/no-explicit-any */
import { ITaggingParams } from '../../../../../store/ducks/tagging/types';
import { Icon } from '@gerdau/hefesto/Icon';

import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Table, Pagination, Tag } from '@gerdau/hefesto-mais-web';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import React from 'react';
import MessageChooseItem from '../../../../../components/MessageChooseItem';
import { formatCurrency } from '../../../../../utils/formatNumbers';
import { ECodeType } from '../../../../../store/ducks/user/types';

interface StockItem {
  item_id: number;
  product_id: number;
  stock_id: number;
  qty: number;
  is_in_stock: boolean;
  is_qty_decimal: boolean;
  show_default_notification_message: boolean;
  use_config_min_qty: boolean;
  min_qty: number;
  use_config_min_sale_qty: number;
  min_sale_qty: number;
  use_config_max_sale_qty: boolean;
  max_sale_qty: number;
  use_config_backorders: boolean;
  backorders: number;
  use_config_notify_stock_qty: boolean;
  notify_stock_qty: number;
  use_config_qty_increments: boolean;
  qty_increments: number;
  use_config_enable_qty_inc: boolean;
  enable_qty_increments: boolean;
  use_config_manage_stock: boolean;
  manage_stock: boolean;
  low_stock_date: string;
  is_decimal_divided: boolean;
  stock_status_changed_auto: number;
}

interface ExtensionAttributes {
  website_ids: number[];
  stock_item: StockItem;
}

interface CustomAttribute {
  attribute_code: string;
  value: string | string[];
}

interface Price {
  plant: string;
  creditPrice: string;
  um: string;
  salesAgreement: string;
  paymentTerm: string;
  paymentTermsDescription: string;
  customerProduct: string;
}

interface Product {
  key: number;
  id: number;
  sku: string;
  name: string;
  attribute_set_id: number;
  status: number;
  visibility: number;
  type_id: string;
  created_at: string;
  updated_at: string;
  extension_attributes: ExtensionAttributes;
  product_links: any[];
  options: any[];
  media_gallery_entries: any[];
  tier_prices: any[];
  custom_attributes: CustomAttribute[];
  price: Price;
  uniqueId: string;
}

const ProductsTable = ({
  products,
  saveProductsToLocalStorage,
  handleAddMultipleProducts,
  onProductUpdate,
  codeType
}: {
  products: Product[];
  saveProductsToLocalStorage: any;
  handleAddMultipleProducts: any;
  onProductUpdate: (products: any[]) => void;
  codeType: ECodeType;
}) => {
  const [localProducts, setLocalProducts] = useState(products);
  const [deletedProductIds, setDeletedProductIds] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(products.length);
  const dispatch = useDispatch();

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const filteredProducts = useMemo(() => {
    return localProducts.filter(
      (product: Product) => !deletedProductIds.has(String(product.uniqueId))
    );
  }, [localProducts, deletedProductIds]);

  const totalFilteredItems = filteredProducts.length;
  const maxPage = Math.ceil(totalFilteredItems / pageSize);

  const notHasPrice = (product: Product) => {
    if (!product.price?.creditPrice) {
      return <Tag variant="danger">Produto indisponível</Tag>;
    }
  };

  useEffect(() => {
    if (currentPage > maxPage && maxPage > 0) {
      setCurrentPage(maxPage);
    }
  }, [maxPage, currentPage]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return filteredProducts
      .slice(startIndex, endIndex)
      .map((product: Product) => ({
        key: product.uniqueId,
        id: product.id,
        codProduct:
          codeType === ECodeType.SKU
            ? product.sku
            : product.price.customerProduct,
        product: product.name,
        price: product.price?.creditPrice
          ? `R$ ${formatCurrency(product.price.creditPrice)}`
          : notHasPrice(product),
        undMeasure: product.price?.um,
        payment:
          product.price?.paymentTermsDescription || product.price?.paymentTerm,
        uniqueId: product.uniqueId
      }));
  }, [filteredProducts, currentPage, pageSize]);

  const columns = useMemo(() => {
    return [
      { title: 'Código do material', dataIndex: 'codProduct' },
      { title: 'Produto', dataIndex: 'product' },
      { title: 'Preço', dataIndex: 'price' },
      { title: 'Unidade de medida', dataIndex: 'undMeasure' },
      { title: 'Pagamento', dataIndex: 'payment' },
      {
        title: '',
        dataIndex: 'actions',
        render: (_: any, record: Product) => (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => handleDelete(String(record.uniqueId))}
          >
            <Icon option="Trash" variant="danger" size="sm" />
          </span>
        )
      }
    ];
  }, []);

  const handleChangePagination = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
    handleAddMultipleProducts({ currentPage: page, pageSize });
  };

  useEffect(() => {
    const taggingProduct: ITaggingParams = {
      event: 'view_item_list',
      ecommerce: {
        screenName: 'comprar_selecionando_itens',
        item_list_name: 'produtos_busca',
        items: paginatedProducts.map((item: any) => ({
          screenName: 'comprar_selecionando_itens',
          etapa: 'adicione_produtos_na_lista',
          item_id: item?.codProduct,
          item_name: item.product,
          price: typeof item.price !== 'string' ? '' : item.price,
          cod_gerdau: item.codProduct,
          unidade_de_medida: item.undMeasure
        }))
      }
    };

    dispatch(sendTagging(taggingProduct));
  }, [paginatedProducts.length]);

  const handleDelete = (uniqueId: string) => {
    handleDeleteTagging(uniqueId);

    setDeletedProductIds((prevDeletedProductIds) => {
      const updatedDeletedProductIds = new Set(prevDeletedProductIds);
      updatedDeletedProductIds.add(uniqueId);

      setLocalProducts((prevProducts: any[]) => {
        const updatedProducts = prevProducts.filter(
          (product: Product) => String(product.uniqueId) !== uniqueId
        );

        if (updatedProducts.length === 0) {
          localStorage.removeItem('selectedProducts');
        } else {
          saveProductsToLocalStorage(updatedProducts);
        }

        if (onProductUpdate) {
          onProductUpdate(updatedProducts);
        }

        return updatedProducts;
      });

      if (currentPage > maxPage && maxPage > 0) {
        setCurrentPage(maxPage);
      }

      return updatedDeletedProductIds;
    });
  };

  function handleDeleteTagging(uniqueId: string) {
    const deletedProduct = products
      .flat(Infinity)
      .find((product: Product) => product.uniqueId === uniqueId);

    dispatch(
      sendTagging({
        event: 'remove_from_cart',
        ecommerce: {
          currency: 'BRL',
          value: deletedProduct?.price?.creditPrice,
          screenName: 'comprar_selecionando_itens',
          items: [
            {
              screenName: 'comprar_selecionando_itens',
              etapa: 'selecione_produtos',
              item_id: String(deletedProduct?.sku),
              item_name: deletedProduct?.name || '',
              price: deletedProduct?.price?.creditPrice,
              item_list_name: 'produtos_adicionados',
              unidade_de_medida: deletedProduct?.price?.um,
              quantity: '1'
            }
          ]
        }
      })
    );
  }

  useEffect(() => {
    if (!paginatedProducts.length && totalFilteredItems > 0) {
      setCurrentPage(1);
    }
  }, [paginatedProducts, totalFilteredItems]);

  if (!paginatedProducts.length) {
    return (
      <>
        <MessageChooseItem message="Sua lista de produtos está vazia. Procure por produtos pelas opções acima." />
      </>
    );
  }
  return (
    <>
      <Table columns={columns} dataSource={paginatedProducts} />
      <Pagination
        total={filteredProducts.length}
        onChange={handleChangePagination}
        pageSizeOptions={[10, 20, 30]}
      />
    </>
  );
};

export default ProductsTable;
